import Base from '../base/base';
import gsap from 'gsap';
import './gallery-viewer.scss';

interface GalleryViewerOptions {
  selector: string;
  lightboxSelector?: string;
  contentSelector?: string;
  openClass?: string;
  debug?: boolean;
}

export default class GalleryViewer extends Base {
  private config: {
    selector: string;
    lightboxSelector: string;
    contentSelector: string;
    openClass: string;
  };

  private container: HTMLElement | null = null;
  private lightbox: HTMLElement | null = null;
  private content: HTMLElement | null = null;
  private items: Array<{ index: number; el: HTMLElement }> = [];

  private boundOnClick: (e: Event) => void;
  private boundOnDimClick: (e: Event) => void;
  private boundOnKey: (e: KeyboardEvent) => void;
  private savedScrollY: number | null = null;

  constructor(options: GalleryViewerOptions) {
    super(options.debug || false);

    this.config = {
      selector: options.selector,
      lightboxSelector: options.lightboxSelector || '.gallery-lightbox',
      contentSelector: options.contentSelector || '.gallery-lightbox__content',
      openClass: options.openClass || 'is-open'
    };

    this.boundOnClick = this.onClick.bind(this);
    this.boundOnDimClick = this.onDimClick.bind(this);
    this.boundOnKey = this.onKey.bind(this);

    this.init();
  }

  init(): void {
    this.log('GalleryViewer init', this.config);
    this.cacheDOM();
    if (!this.container || !this.lightbox || !this.content) {
      this.log('GalleryViewer: required elements not found, aborting');
      return;
    }

    this.scanItems();
    this.bindEvents();
  }

  private cacheDOM(): void {
    this.container = document.querySelector(this.config.selector);
    this.lightbox = document.querySelector(this.config.lightboxSelector);
    this.content = this.lightbox ? (this.lightbox.querySelector(this.config.contentSelector) as HTMLElement) : null;

    this.log('DOM cached', {
      container: !!this.container,
      lightbox: !!this.lightbox,
      content: !!this.content
    });
  }

  private scanItems(): void {
    if (!this.container) return;

    this.items = [];

    // Find thumbnails: <img> elements and the LHG background-image tiles.
    // We do not read or validate image URLs here — elements are assumed to have images.
    const nodes = this.container.querySelectorAll<HTMLElement>('img, .lhg-secondary-image, .lhg-main-image');

    nodes.forEach((el) => {
      el.setAttribute('data-gallery-index', `${this.items.length}`);
      this.items.push({ index: this.items.length, el });
    });

    this.log('Scanned gallery items', { count: this.items.length });
  }

  private bindEvents(): void {
    if (this.container) this.container.addEventListener('click', this.boundOnClick);
    if (this.lightbox) {
      const dim = this.lightbox.querySelector('.gallery-lightbox__dim');
      if (dim) dim.addEventListener('click', this.boundOnDimClick);

      const closeButton = this.lightbox.querySelector('#gallery-lightbox__close');
      if (closeButton) closeButton.addEventListener('click', this.boundOnDimClick);
    }

    this.log('Events bound');
  }

  private onClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target || !this.container) return;

    // Find the nearest element with data-gallery-index
    const thumb = target.closest('[data-gallery-index]') as HTMLElement | null;
    if (thumb) {
      const idx = parseInt(thumb.getAttribute('data-gallery-index') || '0', 10);
      this.open(idx);
    }
  }

  private onDimClick(): void {
    this.close();
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') this.close();
  }

  public open(index: number = 0): void {
    if (!this.lightbox || !this.content || !this.items[index]) return;

    const item = this.items[index];
    // Disable page scroll and show lightbox via class and animate
    this.disableScroll();
    this.lightbox.classList.add(this.config.openClass);

    gsap.fromTo(this.lightbox, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.35, ease: 'power2.out' });

    document.addEventListener('keydown', this.boundOnKey);

    this.log('Gallery opened', { index });

    const ev = new CustomEvent('gallery:open', { detail: { index } });
    document.dispatchEvent(ev);
  }

  public close(): void {
    if (!this.lightbox) return;

    gsap.to(this.lightbox, { autoAlpha: 0, duration: 0.25, ease: 'power2.inOut', onComplete: () => {
      this.lightbox?.classList.remove(this.config.openClass);
      // Re-enable page scroll after hide animation
      this.enableScroll();
    }});

    document.removeEventListener('keydown', this.boundOnKey);

    const ev = new CustomEvent('gallery:close', { detail: {} });
    document.dispatchEvent(ev);
  }

  public destroy(): void {
    if (this.container) this.container.removeEventListener('click', this.boundOnClick);
    if (this.lightbox) {
      const dim = this.lightbox.querySelector('.gallery-lightbox__dim');
      if (dim) dim.removeEventListener('click', this.boundOnDimClick);
    }
    document.removeEventListener('keydown', this.boundOnKey);

    this.items = [];
    this.container = null;
    this.lightbox = null;
    this.content = null;
    // Ensure scrolling is restored if component destroyed while open
    this.enableScroll();
  }

  /**
   * Disable page scrolling while preserving current scroll position
   */
  private disableScroll(): void {
    // Save current scroll position
    this.savedScrollY = window.scrollY || window.pageYOffset || 0;

    // Lock body to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  /**
   * Re-enable page scrolling and restore previous position
   */
  private enableScroll(): void {
    if (this.savedScrollY === null) return;

    // Remove lock styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';

    // Restore scroll position
    window.scrollTo(0, this.savedScrollY);
    this.savedScrollY = null;
  }
}
